import { SCREEN_HELP } from '@hoopoe/shared';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { useLocation } from '@tanstack/react-router';
import { BookOpen, Check, CircleHelp, Keyboard, Route as RouteIcon, Sparkles } from 'lucide-react';
import { useHelpPanel } from '../help/help-panel';
import { Button } from '../ui/button';
import { useHints } from './hints';
import { useTour } from './tour';

const item =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:opacity-50';

/** The ? menu on every screen: help article, tour, shortcuts, hints toggle (PLAN §12.3, §12.6). */
/**
 * The article for the current screen: exact route, else its first two segments (/review/batch/x → /review/batch),
 * else its first segment (e.g. /onboarding/x → /onboarding).
 */
export const helpSlugFor = (pathname: string) => {
  const [, first = '', second] = pathname.split('/');
  return (
    SCREEN_HELP[pathname] ?? (second ? SCREEN_HELP[`/${first}/${second}`] : undefined) ?? SCREEN_HELP[`/${first}`] ?? 'getting-started'
  );
};

export function HelpMenu() {
  const hints = useHints();
  const panel = useHelpPanel();
  const { pathname } = useLocation();
  const tour = useTour();
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label="Help">
          <CircleHelp />
        </Button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="end" sideOffset={6} className="z-50 min-w-56 rounded-md border bg-surface-raised p-1 shadow-md">
          <Menu.Item className={item} onSelect={() => panel.open(helpSlugFor(pathname))}>
            <BookOpen className="size-4" aria-hidden="true" /> Help for this screen
          </Menu.Item>
          <Menu.Item className={item} onSelect={() => panel.open('whats-new')}>
            <Sparkles className="size-4" aria-hidden="true" /> What's new
          </Menu.Item>
          <Menu.Item className={item} disabled={!tour.current} onSelect={() => tour.current && tour.start(tour.current)}>
            <RouteIcon className="size-4" aria-hidden="true" /> Take the tour
          </Menu.Item>
          <Menu.Item className={item} onSelect={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }))}>
            <Keyboard className="size-4" aria-hidden="true" /> Keyboard shortcuts
            <kbd className="ms-auto rounded border bg-muted px-1 font-mono text-xs">?</kbd>
          </Menu.Item>
          <Menu.Separator className="my-1 h-px bg-border" />
          <Menu.CheckboxItem className={item} checked={hints.showHints} onCheckedChange={v => hints.setShowHints(v === true)}>
            <span className="grid size-4 place-items-center">
              <Menu.ItemIndicator>
                <Check className="size-4" aria-hidden="true" />
              </Menu.ItemIndicator>
            </span>
            Show hints
          </Menu.CheckboxItem>
          <Menu.Item className={item} onSelect={() => hints.resetAll()}>
            <span className="size-4" /> Show dismissed tips again
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
